// Generated by ReScript, PLEASE EDIT WITH CARE

import * as React from "react";
import * as LogUtils from "./LogUtils.bs.jsx";
import * as Stdlib_Promise from "@rescript/runtime/lib/es6/Stdlib_Promise.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";

function usePromise(fn) {
  let match = React.useState(() => ({
    state: "idle",
    data: undefined,
    error: undefined
  }));
  let setResult = match[1];
  let run = () => {
    setResult(prevResult => ({
      state: "pending",
      data: prevResult.data,
      error: undefined
    }));
    Stdlib_Promise.$$catch(fn().then(data => {
      setResult(param => ({
        state: "fulfilled",
        data: Primitive_option.some(data),
        error: undefined
      }));
      return Promise.resolve();
    }), error => {
      setResult(param => ({
        state: "rejected",
        data: undefined,
        error: error
      }));
      return Promise.resolve();
    });
  };
  return [
    match[0],
    run
  ];
}

function overflowingReducer(state, action) {
  let newState = state;
  let thresholds = state.thresholds;
  let currentLayoutIndex = state.currentLayoutIndex;
  let contentWidth = action.contentWidth;
  let availableWidth = action.availableWidth;
  let overflowing = contentWidth > availableWidth;
  if (overflowing) {
    let currentLayoutThreshold = thresholds[currentLayoutIndex];
    if (contentWidth !== currentLayoutThreshold) {
      thresholds.splice(currentLayoutIndex, 1, contentWidth);
      if (import.meta.env.DEV && currentLayoutIndex !== 0) {
        let widerLayoutThreshold = thresholds[currentLayoutIndex - 1 | 0];
        if (contentWidth > widerLayoutThreshold) {
          LogUtils.captureException(`The threshold for layout "` + state.layouts[currentLayoutIndex] + `" (` + contentWidth.toString() + `px) is wider than threshold for layout "` + state.layouts[currentLayoutIndex - 1 | 0] + `" (` + widerLayoutThreshold.toString() + `px)
                    
The layouts should be sorted from widest to narrowest (e.g. ["XL", "MD", "SM"]). This likely means an error in the rendering logic.`);
        }
      }
    }
    let canShrink = currentLayoutIndex !== (state.layouts.length - 1 | 0);
    if (canShrink) {
      newState = {
        currentLayoutIndex: currentLayoutIndex + 1 | 0,
        layouts: state.layouts,
        thresholds: state.thresholds
      };
    }
  } else {
    let canGrow = currentLayoutIndex !== 0;
    if (canGrow) {
      let widerLayoutThreshold$1 = thresholds[currentLayoutIndex - 1 | 0];
      if (availableWidth > widerLayoutThreshold$1) {
        newState = {
          currentLayoutIndex: state.currentLayoutIndex - 1 | 0,
          layouts: state.layouts,
          thresholds: state.thresholds
        };
      }
    }
  }
  return newState;
}

function useIsHorizontallyOverflowing(element, layouts) {
  let match = React.useReducer(overflowingReducer, {
    currentLayoutIndex: 0,
    layouts: layouts,
    thresholds: []
  });
  let sendOverflowing = match[1];
  React.useEffect(() => {
    if (element == null) {
      return;
    }
    let testOverflowing = param => {
      let availableWidth = element.clientWidth;
      let contentWidth = element.scrollWidth;
      sendOverflowing({
        TAG: "Resized",
        availableWidth: availableWidth,
        contentWidth: contentWidth
      });
    };
    testOverflowing();
    let resizeObserver = new ResizeObserver(testOverflowing);
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [element]);
  return layouts[match[0].currentLayoutIndex];
}

export {
  usePromise,
  overflowingReducer,
  useIsHorizontallyOverflowing,
}
/* react Not a pure module */
